/**
 * moonshot-collection-search.js
 *
 * Per-collection free-text search widget. Renders a debounced typeahead
 * dropdown that calls Storefront API `collection.products(query:)` —
 * results are scoped to the current collection by construction.
 *
 * Why this exists: Shopify's native Search & Discovery plugin handles
 * facet filters well (price, tags, metafields) but does NOT offer a
 * free-text search box that's restricted to a single collection. For
 * large collections like Magic Singles (50k+ products), customers need
 * to find a specific card by name without leaving the collection page.
 *
 * Architecture:
 *   - Reads collection handle from a data attribute on the host element.
 *   - Debounces input (250ms) to avoid hammering the API while typing.
 *   - Uses `M.storefront(...)` from moonshot-common.js for token + auth.
 *   - Renders top N results in a panel below the input. Clicking a
 *     result navigates to the PDP. Pressing Enter (or clicking "See all")
 *     navigates to the collection page with `?q=<term>&type=product`
 *     which Shopify's native search interprets.
 *   - Closes on outside click, Escape, or selection.
 *
 * Tradeoffs vs Algolia (deferred per memory algolia-paused-for-sync.md):
 *   - Native Shopify search is substring/prefix on title — not typo-
 *     tolerant. "Sol Ring" works; "Sol Rng" doesn't. Acceptable for
 *     card-name searches where the user knows what they're looking for.
 *   - No relevance scoring beyond Shopify's default order. Results come
 *     back as Shopify orders them within the collection.
 *
 * Loaded via `<script defer>` in sections/moonshot-collection-search.liquid.
 * Depends on moonshot-common.js being loaded earlier (provides M.storefront).
 */
(function () {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const DEBOUNCE_MS = 250;
  const MIN_QUERY_LENGTH = 2;
  const DEFAULT_MAX_RESULTS = 10;
  const PANEL_ROLE = "listbox";
  const ITEM_ROLE = "option";

  // Storefront API has no native collection-scoped free-text search,
  // so we use a TWO-STAGE hybrid:
  //
  //   Stage 1 (preferred): fetch up to 250 products in the current
  //     collection and substring-match titles client-side. Perfect
  //     recall for small/curated collections (Moonshot Rips lists).
  //
  //   Stage 2 (fallback): if Stage 1 returned exactly 250 products
  //     AND there were 0 matches, the collection is larger than our
  //     cap — fall through to storewide products(query:) with a
  //     post-filter on the collection list. Acceptable for large
  //     collections like Magic Singles where matches are tightly
  //     bounded by specific card names.
  //
  // Earlier attempts used Storefront's root `search` with a
  // `collection: $id` productFilter — that field does not exist on
  // ProductFilter. Logged as the failure 2026-05-26 / 2026-05-27.

  const COLLECTION_QUERY = [
    "query CollProducts($handle: String!, $n: Int!) {",
    "  collection(handle: $handle) {",
    "    products(first: $n) {",
    "      nodes {",
    "        id",
    "        title",
    "        handle",
    "        featuredImage { url altText }",
    "        priceRange { minVariantPrice { amount currencyCode } maxVariantPrice { amount currencyCode } }",
    "      }",
    "      pageInfo { hasNextPage }",
    "    }",
    "  }",
    "}",
  ].join("\n");

  const STOREWIDE_QUERY = [
    "query StorewideProducts($q: String!, $n: Int!) {",
    "  products(query: $q, first: $n) {",
    "    nodes {",
    "      id",
    "      title",
    "      handle",
    "      featuredImage { url altText }",
    "      priceRange { minVariantPrice { amount currencyCode } maxVariantPrice { amount currencyCode } }",
    "      collections(first: 25) { nodes { id } }",
    "    }",
    "  }",
    "}",
  ].join("\n");

  const COLLECTION_FETCH_CAP = 250; // Storefront's per-call max
  const STOREWIDE_FETCH_CAP = 100;

  // User's raw input is the query string. Shopify's search-syntax
  // supports prefix matching with `*` and AND-joining; we wrap each
  // whitespace-separated term to give prefix-matching ("Sol R" →
  // "Sol Ring"). title: field targets product titles specifically;
  // omitting the field name searches all indexed fields including
  // body and tags. We use the unqualified form for broader recall.
  function buildQueryString(input) {
    const cleaned = input.trim().replace(/[^\w\s'-]/g, " ");
    if (!cleaned) return "";
    return cleaned
      .split(/\s+/)
      .filter(Boolean)
      .map(function (term) { return term + "*"; })
      .join(" AND ");
  }

  function formatPrice(amount, currencyCode) {
    if (amount == null) return "";
    const n = Number(amount);
    if (!Number.isFinite(n)) return "";
    // We don't bother with Intl — the values come back as decimal
    // strings like "12.99". Just prefix with $ for USD; for any other
    // currency, append the code. Moonshot is USD-only as of now.
    if (currencyCode === "USD" || !currencyCode) return "$" + n.toFixed(2);
    return n.toFixed(2) + " " + currencyCode;
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function renderResults(container, products, query, collectionHandle) {
    container.innerHTML = "";
    if (!products || products.length === 0) {
      const empty = document.createElement("li");
      empty.className = "moonshot-coll-search__empty";
      empty.textContent = 'No matches for "' + query + '" in this collection.';
      container.appendChild(empty);
      return;
    }
    for (let i = 0; i < products.length; i++) {
      const p = products[i];
      const minP = p.priceRange && p.priceRange.minVariantPrice;
      const maxP = p.priceRange && p.priceRange.maxVariantPrice;
      const sameMinMax =
        minP && maxP && Number(minP.amount) === Number(maxP.amount);
      const priceStr = !minP
        ? ""
        : sameMinMax
        ? formatPrice(minP.amount, minP.currencyCode)
        : "from " + formatPrice(minP.amount, minP.currencyCode);
      const imgHtml =
        p.featuredImage && p.featuredImage.url
          ? '<img class="moonshot-coll-search__thumb" loading="lazy" src="' +
            escapeHtml(p.featuredImage.url) +
            '" alt="' +
            escapeHtml(p.featuredImage.altText || p.title) +
            '" />'
          : '<span class="moonshot-coll-search__thumb moonshot-coll-search__thumb--placeholder" aria-hidden="true"></span>';

      const li = document.createElement("li");
      li.className = "moonshot-coll-search__result";
      li.setAttribute("role", ITEM_ROLE);
      li.setAttribute("data-handle", p.handle);
      li.innerHTML =
        '<a class="moonshot-coll-search__result-link" href="/products/' +
        encodeURIComponent(p.handle) +
        '">' +
        imgHtml +
        '<span class="moonshot-coll-search__result-text">' +
        '<span class="moonshot-coll-search__result-title">' +
        escapeHtml(p.title) +
        "</span>" +
        (priceStr
          ? '<span class="moonshot-coll-search__result-price">' +
            escapeHtml(priceStr) +
            "</span>"
          : "") +
        "</span>" +
        "</a>";
      container.appendChild(li);
    }
  }

  function init(host) {
    if (!host || host.dataset.moonshotCollSearchInit === "1") return;
    host.dataset.moonshotCollSearchInit = "1";

    const collectionHandle = host.dataset.collectionHandle;
    const collectionId = host.dataset.collectionId;
    const collectionTitle = host.dataset.collectionTitle || "this collection";
    const maxResults =
      parseInt(host.dataset.maxResults || "", 10) || DEFAULT_MAX_RESULTS;
    if (!collectionHandle || !collectionId) {
      console.warn(
        "[moonshot-coll-search] missing data-collection-handle or data-collection-id",
      );
      return;
    }

    const input = host.querySelector("[data-moonshot-coll-search-input]");
    const panel = host.querySelector("[data-moonshot-coll-search-panel]");
    const resultsEl = host.querySelector("[data-moonshot-coll-search-results]");
    const footerEl = host.querySelector("[data-moonshot-coll-search-footer]");
    if (!input || !panel || !resultsEl) {
      console.warn("[moonshot-coll-search] missing required child elements");
      return;
    }

    let debounceTimer = null;
    let activeRequestId = 0;

    function closePanel() {
      panel.hidden = true;
      input.setAttribute("aria-expanded", "false");
    }
    function openPanel() {
      panel.hidden = false;
      input.setAttribute("aria-expanded", "true");
    }

    function navigateToFullSearch(q) {
      if (!q) return;
      const url =
        "/collections/" +
        encodeURIComponent(collectionHandle) +
        "?q=" +
        encodeURIComponent(q) +
        "&type=product";
      window.location.href = url;
    }

    // Cache the in-collection product set per init. Fetched lazily
    // on first query, reused on subsequent keystrokes. For 250-cap
    // collections this is the entire set; the filter is purely
    // client-side after the initial fetch.
    let collectionProductsCache = null;   // Array<Product> | null
    let collectionExhausted = false;       // false if hasNextPage === true

    async function fetchCollectionProducts(M) {
      if (collectionProductsCache) return collectionProductsCache;
      const data = await M.storefront(COLLECTION_QUERY, {
        handle: collectionHandle,
        n: COLLECTION_FETCH_CAP,
      });
      const nodes = (data && data.collection && data.collection.products && data.collection.products.nodes) || [];
      const hasMore =
        !!(data && data.collection && data.collection.products && data.collection.products.pageInfo && data.collection.products.pageInfo.hasNextPage);
      collectionProductsCache = nodes;
      collectionExhausted = !hasMore;
      return nodes;
    }

    function clientFilter(nodes, lowerTerm) {
      const out = [];
      for (let i = 0; i < nodes.length && out.length < maxResults; i++) {
        const n = nodes[i];
        if (!n || !n.title) continue;
        if (n.title.toLowerCase().indexOf(lowerTerm) !== -1) {
          out.push(n);
        }
      }
      return out;
    }

    // Shared render path — products list, footer ("See all" CTA only
    // when results may be truncated), open the panel.
    function renderAndOpen(products, term, mayBeTruncated) {
      renderResults(resultsEl, products, term, collectionHandle);
      if (footerEl) {
        const showSeeAll = mayBeTruncated && products.length >= maxResults;
        if (showSeeAll) {
          footerEl.hidden = false;
          footerEl.innerHTML =
            '<button type="button" class="moonshot-coll-search__see-all" data-moonshot-coll-search-see-all>' +
            "See all matches in " +
            escapeHtml(collectionTitle) +
            "</button>";
          const btn = footerEl.querySelector("[data-moonshot-coll-search-see-all]");
          if (btn) btn.addEventListener("click", function () { navigateToFullSearch(term); });
        } else {
          footerEl.hidden = true;
          footerEl.innerHTML = "";
        }
      }
      openPanel();
    }

    async function runQuery(rawTerm) {
      const term = rawTerm.trim();
      if (term.length < MIN_QUERY_LENGTH) {
        closePanel();
        return;
      }
      const reqId = ++activeRequestId;
      try {
        const M = window.Moonshot;
        if (!M || typeof M.storefront !== "function") {
          console.warn("[moonshot-coll-search] window.Moonshot.storefront not ready");
          return;
        }

        // ── Stage 1: in-collection fetch (cached) + client-side filter ──
        const lowerTerm = term.toLowerCase();
        const collNodes = await fetchCollectionProducts(M);
        if (reqId !== activeRequestId) return;

        const stage1 = clientFilter(collNodes, lowerTerm);

        // If we have full collection coverage (≤250 products), trust
        // Stage 1 even when empty — there are no other matches.
        if (collectionExhausted) {
          renderAndOpen(stage1, term, stage1.length < maxResults);
          return;
        }

        // Stage 1 found matches in a partial set → show them. For
        // typical card searches in big collections, first-250 matches
        // cover the most common cases. Stage 2 is reserved for the
        // 0-match-in-first-250 fallback.
        if (stage1.length > 0) {
          renderAndOpen(stage1, term, true);
          return;
        }

        // ── Stage 2: storewide search + post-filter to collection ──
        const q = buildQueryString(term);
        if (!q) {
          renderAndOpen([], term, false);
          return;
        }
        const sw = await M.storefront(STOREWIDE_QUERY, {
          q: q,
          n: STOREWIDE_FETCH_CAP,
        });
        if (reqId !== activeRequestId) return;
        const swNodes = (sw && sw.products && sw.products.nodes) || [];
        const products = [];
        for (let i = 0; i < swNodes.length && products.length < maxResults; i++) {
          const n = swNodes[i];
          if (!n || !n.handle) continue;
          const colls = (n.collections && n.collections.nodes) || [];
          let inCollection = false;
          for (let j = 0; j < colls.length; j++) {
            if (colls[j] && colls[j].id === collectionId) {
              inCollection = true;
              break;
            }
          }
          if (inCollection) products.push(n);
        }
        renderAndOpen(products, term, true);
      } catch (err) {
        if (reqId !== activeRequestId) return;
        console.error("[moonshot-coll-search] query failed:", err);
        resultsEl.innerHTML =
          '<li class="moonshot-coll-search__error">' +
          "Search is unavailable right now. Browse the collection below." +
          "</li>";
        openPanel();
      }
    }

    input.addEventListener("input", function () {
      const term = input.value;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (!term || term.trim().length < MIN_QUERY_LENGTH) {
        closePanel();
        return;
      }
      debounceTimer = setTimeout(function () { runQuery(term); }, DEBOUNCE_MS);
    });

    input.addEventListener("keydown", function (e) {
      if (e.key === "Enter") {
        e.preventDefault();
        navigateToFullSearch(input.value.trim());
      } else if (e.key === "Escape") {
        closePanel();
        input.blur();
      }
    });

    input.addEventListener("focus", function () {
      if (input.value.trim().length >= MIN_QUERY_LENGTH && resultsEl.children.length) {
        openPanel();
      }
    });

    document.addEventListener("click", function (e) {
      if (!host.contains(e.target)) closePanel();
    });
  }

  function initAll() {
    const hosts = document.querySelectorAll("[data-moonshot-coll-search]");
    for (let i = 0; i < hosts.length; i++) init(hosts[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
